<template>
  <div class="audit-main-container" :class="$vuetify.theme.dark ? 'theme--dark-bg' : 'grey lighten-4'">
    <!-- CABECERA PROFESIONAL 🏛️ -->
    <v-toolbar flat height="70" class="audit-header px-4">
      <div class="d-flex align-center">
        <v-icon x-large :color="$vuetify.theme.dark ? 'teal lighten-2' : 'blue-grey darken-4'" class="mr-3">mdi-finance</v-icon>
        <div>
          <h1 class="audit-title mb-0" :class="$vuetify.theme.dark ? 'white--text' : 'grey--text text--darken-4'" style="line-height: 1;">AUDITORÍA CONTABLE</h1>
          <span class="caption grey--text font-weight-bold uppercase text-overline" style="letter-spacing: 1px !important;">Inteligencia de Ventas vs Compras</span>
        </div>
      </div>
      <v-spacer />
      <v-btn icon :color="$vuetify.theme.dark ? 'white' : 'grey darken-3'" @click="$router.go(-1)" large>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- PANEL DE FILTROS 🔍 -->
    <v-sheet class="filter-dense-panel mb-2 pa-4 mx-2 mt-2 rounded-xl" elevation="1">
      <v-form ref="form" v-model="valid">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <v-dialog ref="dialog" v-model="dateModal" :return-value.sync="dates" persistent width="290px">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dates"
                  label="Periodo de Auditoría"
                  prepend-inner-icon="mdi-calendar-range"
                  outlined dense hide-details rounded
                  readonly class="audit-input"
                  v-bind="attrs" v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dates" range color="blue-grey darken-3">
                <v-spacer></v-spacer>
                <v-btn text color="grey darken-1" @click="dateModal = false">Cancelar</v-btn>
                <v-btn text color="blue-grey darken-4" @click="$refs.dialog.save(dates)">Aceptar</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-col>
          
          <v-col cols="12" sm="8" md="9" class="d-flex align-center">
            <v-btn color="blue-grey darken-4" dark height="38" @click="getReport" :disabled="!valid" class="audit-btn px-6 shadow-sm mr-2 rounded-lg">
              <v-icon left>mdi-magnify</v-icon> Generar Auditoría
            </v-btn>
            <v-divider vertical class="mx-3" height="24" :dark="$vuetify.theme.dark" />
            <v-btn color="success darken-1" dark height="38" @click="downloadReport" :disabled="!isReportGenerated" class="audit-btn px-4 shadow-sm mr-2 rounded-lg">
              <v-icon left>mdi-microsoft-excel</v-icon> Excel
            </v-btn>
            <v-btn color="primary darken-1" dark height="38" @click="sendEmailReport" :disabled="!validEnviarCorreo" class="audit-btn px-4 shadow-sm rounded-lg">
              <v-icon left size="18">mdi-email-send-outline</v-icon> Contador
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-sheet>

    <!-- SCORECARDS RENTABILIDAD 💰🚀 -->
    <transition name="fade">
      <v-row v-if="isReportGenerated" class="mx-1 mb-2 px-2 mt-4">
        <v-col cols="12" sm="4">
          <v-card :class="$vuetify.theme.dark ? 'score-card-dark' : 'score-card-light'" class="rounded-xl pa-5 elevation-2 d-flex align-center shadow-premium">
            <div class="score-icon-wrap bg-success-soft mr-4">
              <v-icon color="success" size="32">mdi-currency-usd</v-icon>
            </div>
            <div>
              <p class="score-label mb-0">VENTAS TOTALES</p>
              <h2 class="score-value success--text">₡{{ formatPrice(totalsVentas) }}</h2>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card :class="$vuetify.theme.dark ? 'score-card-dark' : 'score-card-light'" class="rounded-xl pa-5 elevation-2 d-flex align-center shadow-premium">
            <div class="score-icon-wrap bg-warning-soft mr-4">
              <v-icon color="orange lighten-1" size="32">mdi-cart-arrow-down</v-icon>
            </div>
            <div>
              <p class="score-label mb-0">COMPRAS TOTALES</p>
              <h2 class="score-value orange--text">₡{{ formatPrice(totalsInventarioGastos) }}</h2>
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="4">
          <v-card :class="$vuetify.theme.dark ? 'score-card-dark' : 'score-card-light'" class="rounded-xl pa-5 elevation-2 d-flex align-center shadow-premium">
            <div class="score-icon-wrap mr-4" :class="totalsVentas - totalsInventarioGastos >= 0 ? 'bg-info-soft' : 'bg-error-soft'">
              <v-icon :color="totalsVentas - totalsInventarioGastos >= 0 ? 'teal lighten-1' : 'error'" size="32">mdi-scale-balance</v-icon>
            </div>
            <div>
              <p class="score-label mb-0">BALANCE NETO</p>
              <h2 class="score-value" :class="totalsVentas - totalsInventarioGastos >= 0 ? 'teal--text text--lighten-2' : 'error--text'">
                ₡{{ formatPrice(Math.abs(totalsVentas - totalsInventarioGastos)) }}
                <v-icon small class="ml-1">{{ totalsVentas - totalsInventarioGastos >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
              </h2>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </transition>

    <!-- GRÁFICOS KPI 📈 -->
    <transition name="fade">
      <v-row v-if="isReportGenerated" class="mx-1 mb-2 px-2">
        <v-col cols="12" md="6">
          <v-card :class="$vuetify.theme.dark ? 'audit-chart-card-dark' : 'audit-chart-card-light'" class="rounded-xl elevation-1 pa-4 d-flex flex-column" style="min-height: 400px;">
            <div class="text-center mb-1">
              <h3 class="text-subtitle-1 font-weight-black mb-0" :class="$vuetify.theme.dark ? 'teal--text text--lighten-3' : 'blue-grey--text text--darken-4'">DISTRIBUCIÓN DE VENTAS</h3>
              <span class="caption grey--text">Por condición de venta (Contado vs Crédito)</span>
            </div>
            <div class="chart-wrapper flex-grow-1 d-flex justify-center align-center" style="position: relative;">
              <Pie 
                v-if="chartDataSales && chartDataSales.labels && chartDataSales.labels.length > 0" 
                :chart-data="chartDataSales" 
                :chart-options="chartOptions" 
                :key="'sales-' + items.length + ($vuetify.theme.dark ? 'd' : 'l')"
                :height="260"
              />
            </div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card :class="$vuetify.theme.dark ? 'audit-chart-card-dark' : 'audit-chart-card-light'" class="rounded-xl elevation-1 pa-4 d-flex flex-column" style="min-height: 400px;">
            <div class="text-center mb-1">
              <h3 class="text-subtitle-1 font-weight-black mb-0" :class="$vuetify.theme.dark ? 'orange--text text--lighten-3' : 'orange--text text--darken-4'">ANÁLISIS DE COMPRAS</h3>
              <span class="caption grey--text">Clasificación inteligente (Mercadería vs Gastos)</span>
            </div>
            <div class="chart-wrapper flex-grow-1 d-flex justify-center align-center" style="position: relative;">
              <Pie 
                v-if="chartDataPurchases && chartDataPurchases.labels && chartDataPurchases.labels.length > 0" 
                :chart-data="chartDataPurchases" 
                :chart-options="chartOptions" 
                :key="'purchases-' + items2.length + ($vuetify.theme.dark ? 'd' : 'l')"
                :height="260"
              />
            </div>
          </v-card>
        </v-col>
      </v-row>
    </transition>

    <!-- BARRA DE RESUMEN INTELIGENTE 💰 -->
    <transition name="fade">
      <v-row v-if="isReportGenerated" class="mx-1 mb-4 px-2 no-gutters">
        <v-col cols="12">
          <div class="summary-bar-container d-flex flex-wrap elevation-1 rounded-xl overflow-hidden shadow-premium">
             <div class="summary-block flex-grow-1 d-flex flex-column py-2 px-6 border-right-premium">
               <span class="summary-label-mini">Inv. Mercadería</span>
               <span class="summary-value-mini font-weight-black" :class="$vuetify.theme.dark ? 'indigo--text text--lighten-3' : 'indigo--text text--darken-4'">₡{{ formatPrice(totalManualMerc + totalAutoMerc) }}</span>
             </div>
             <div class="summary-block flex-grow-1 d-flex flex-column py-2 px-6 border-right-premium">
               <span class="summary-label-mini">Gastos / Servicios</span>
               <span class="summary-value-mini font-weight-black" :class="$vuetify.theme.dark ? 'orange--text text--lighten-3' : 'orange--text text--darken-4'">₡{{ formatPrice(totalManualGasto + totalAutoGasto) }}</span>
             </div>
             <div class="summary-block flex-grow-1 d-flex flex-column py-2 px-6 bg-summary-total-sales shadow-inner-premium">
               <span class="summary-label-mini white--text opacity-70">VENTAS TOTALES</span>
               <span class="summary-value-mini white--text font-weight-black text-h6">₡{{ formatPrice(totalsVentas) }}</span>
             </div>
          </div>
        </v-col>
      </v-row>
    </transition>

    <!-- TABLAS AUDITORÍA 📊 -->
    <v-card flat class="mx-2 rounded-xl overflow-hidden shadow-premium border-0 mb-16" :class="$vuetify.theme.dark ? 'card-dark-bg' : ''">
      <v-tabs v-model="tab2" background-color="blue-grey darken-4" dark slider-color="teal accent-3" grow height="54">
        <v-tab key="emision" class="font-weight-black">EMITIDOS (VENTAS)</v-tab>
        <v-tab key="recibidos" class="font-weight-black">RECIBIDOS (COMPRAS)</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab2" :class="$vuetify.theme.dark ? 'theme--dark-bg' : ''">
        <v-tab-item key="emision">
          <v-data-table :headers="headers" :items="items" class="audit-table audit-zebra" fixed-header height="500px" hide-default-footer disable-pagination>
            <template v-slot:item.totalComprobanteStr="{ item }">
              <span class="font-weight-black" :class="$vuetify.theme.dark ? 'white--text' : 'blue-grey--text text--darken-4'">{{ item.totalComprobanteStr }}</span>
            </template>
            <template v-slot:item.pdf="{ item }">
              <v-btn icon color="error" small @click="viewPDF(item)" v-if="item.clavehacienda"><v-icon>mdi-file-pdf-box</v-icon></v-btn>
            </template>
            <template v-slot:item.enviado="{ item }">
              <v-btn icon color="primary" small @click="viewEnviado(item)" v-if="item.clavehacienda"><v-icon>mdi-file-export</v-icon></v-btn>
            </template>
            <template v-slot:item.recibido="{ item }">
              <v-btn icon color="teal" small @click="viewRecibido(item)" v-if="item.clavehacienda"><v-icon>mdi-file-import</v-icon></v-btn>
            </template>
          </v-data-table>
          <!-- FOOTER EMITIDOS -->
          <div v-if="items.length > 0" class="rc-footer" :class="$vuetify.theme.dark ? 'rc-footer--dark' : ''">
            <div class="rc-footer__row rc-footer__row--summary">
              <div class="rc-footer__item">
                <span class="rc-footer__label">SUBTOTAL</span>
                <v-chip small label dark color="blue-grey darken-3" class="font-weight-black ml-1">₡ {{ emitidosTotals.subTotal }}</v-chip>
              </div>
              <div class="rc-footer__item">
                <span class="rc-footer__label">DESCUENTOS</span>
                <v-chip small label dark color="deep-orange darken-3" class="font-weight-black ml-1">₡ {{ emitidosTotals.descuentos }}</v-chip>
              </div>
              <div class="rc-footer__item">
                <span class="rc-footer__label">TOTAL IVA</span>
                <v-chip small label dark color="indigo darken-2" class="font-weight-black ml-1">₡ {{ emitidosTotals.totalIVA }}</v-chip>
              </div>
              <div class="rc-footer__item">
                <span class="rc-footer__label">TOTAL COMPROBANTE</span>
                <v-chip small label dark color="teal darken-3" class="font-weight-black ml-1">₡ {{ emitidosTotals.totalComprobante }}</v-chip>
              </div>
              <v-spacer />
              <span class="rc-footer__count">{{ items.length }} REGISTROS</span>
            </div>
          </div>
        </v-tab-item>

        <v-tab-item key="recibidos">
          <v-data-table :headers="headers2" :items="items2" class="audit-table audit-zebra" fixed-header height="500px" hide-default-footer disable-pagination>
             <template v-slot:item.clasificacion="{ item }">
              <v-chip
                small
                label
                class="font-weight-black"
                :color="item.clasificacion === 'M' ? 'indigo darken-2' : 'deep-orange darken-3'"
                dark
              >
                {{ item.clasificacion === 'M' ? 'MERCADERÍA' : 'GASTO / SERV' }}
              </v-chip>
            </template>
            <template v-slot:item.totalComprobanteStr="{ item }">
              <span class="font-weight-black" :class="$vuetify.theme.dark ? 'amber--text text--lighten-2' : 'teal--text text--darken-4'">{{ item.totalComprobanteStr }}</span>
            </template>
            <template v-slot:item.xmlRecibido="{ item }">
              <v-btn icon color="indigo" small @click="viewXMLRecibido(item)" v-if="item.clavehacienda"><v-icon>mdi-file-import</v-icon></v-btn>
            </template>
            <template v-slot:item.xmlFirmado="{ item }">
              <v-btn icon color="orange darken-2" small @click="viewXMLFirmado(item)" v-if="item.clavehacienda"><v-icon>mdi-file-export</v-icon></v-btn>
            </template>
            <template v-slot:item.xmlRespuesta="{ item }">
              <v-btn icon color="teal" small @click="viewXMLRespuesta(item)" v-if="item.clavehacienda"><v-icon>mdi-reply-all</v-icon></v-btn>
            </template>
          </v-data-table>
          <!-- FOOTER RECIBIDOS -->
          <div v-if="items2.length > 0" class="rc-footer" :class="$vuetify.theme.dark ? 'rc-footer--dark' : ''">
            <div class="rc-footer__row rc-footer__row--technical">
              <div class="rc-footer__item">
                <span class="rc-footer__label">MERCADERÍA</span>
                <span class="rc-footer__value indigo--text text--lighten-2 font-weight-black">₡ {{ recibidosTotals.totalMerc }}</span>
              </div>
              <v-divider vertical class="mx-3" />
              <div class="rc-footer__item">
                <span class="rc-footer__label">GASTOS / SERVICIOS</span>
                <span class="rc-footer__value deep-orange--text font-weight-black">₡ {{ recibidosTotals.totalGasto }}</span>
              </div>
            </div>
            <div class="rc-footer__row rc-footer__row--summary">
              <div class="rc-footer__item">
                <span class="rc-footer__label">SUBTOTAL</span>
                <v-chip small label dark color="blue-grey darken-3" class="font-weight-black ml-1">₡ {{ recibidosTotals.subTotal }}</v-chip>
              </div>
              <div class="rc-footer__item">
                <span class="rc-footer__label">DESCUENTOS</span>
                <v-chip small label dark color="deep-orange darken-3" class="font-weight-black ml-1">₡ {{ recibidosTotals.descuentos }}</v-chip>
              </div>
              <div class="rc-footer__item">
                <span class="rc-footer__label">TOTAL IVA</span>
                <v-chip small label dark color="indigo darken-2" class="font-weight-black ml-1">₡ {{ recibidosTotals.totalIVA }}</v-chip>
              </div>
              <div class="rc-footer__item">
                <span class="rc-footer__label">TOTAL COMPROBANTE</span>
                <v-chip small label dark color="teal darken-3" class="font-weight-black ml-1">₡ {{ recibidosTotals.totalComprobante }}</v-chip>
              </div>
              <v-spacer />
              <span class="rc-footer__count">{{ items2.length }} REGISTROS</span>
            </div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- VISOR DE DOCUMENTOS 📄 -->
    <v-dialog v-model="docViewerDialog" max-width="1100" eager>
      <v-card style="height: 88vh;" class="d-flex flex-column overflow-hidden rounded-xl">
        <div class="doc-viewer-toolbar d-flex align-center px-3 py-2 white--text flex-shrink-0">
          <v-icon color="white" size="18" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="font-weight-bold text-subtitle-2">{{ docViewerTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon x-small dark class="mr-1" @click="abrirEnNuevaPestana" title="Abrir en nueva pestaña">
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
          <v-btn icon x-small dark @click="docViewerDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>
        <iframe v-if="docViewerDialog" :src="docViewerUrl" class="flex-grow-1" style="border: none; width: 100%;"></iframe>
      </v-card>
    </v-dialog>

    <Snackbar v-bind:text="snackText" v-bind:snackbar.sync="snackValue" />
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";
import Snackbar from "./shared/Snackbar.vue";
import { Pie } from "vue-chartjs/legacy";
import {
  Chart as ChartJS, Title, Tooltip, Legend,
  ArcElement, CategoryScale, LinearScale
} from "chart.js";

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, LinearScale);

export default {
  components: { Snackbar, Pie },
  data() {
    return {
      valid: false, validEnviarCorreo: false, dateModal: false, dates: [],
      items: [], items2: [], opcionesSistemaCia: [], snackText: "", snackValue: false, tab2: 0,
      docViewerDialog: false, docViewerUrl: '', docViewerTitle: '',
      totalsVentas: 0, totalsInventarioGastos: 0,
      totalManualMerc: 0, totalManualGasto: 0, totalAutoMerc: 0, totalAutoGasto: 0,
      chartDataSales: { labels: [], datasets: [] },
      chartDataPurchases: { labels: [], datasets: [] },
      headers: [
        { text: "Clave Hacienda", align: "start", value: "clavehacienda", width: 450 },
        { text: "Emisor", align: "start", value: "emisorNombre", width: 300 },
        { text: "Cédula Emisor", align: "start", value: "cia", width: 140 },
        { text: "Receptor", align: "start", value: "clienteNombre", width: 300 },
        { text: "Cédula Receptor", align: "start", value: "clienteCedula", width: 140 },
        { text: "Fecha", align: "start", value: "fechaString", width: 110 },
        { text: "Moneda", align: "start", value: "moneda", width: 90 },
        { text: "Estado Hacienda", align: "start", value: "estadoHacienda", width: 160 },
        { text: "Consecutivo", align: "start", value: "consHacReal", width: 200 },
        { text: "Documento", align: "start", value: "tipoDocumento", width: 220 },
        { text: "Sub Total", align: "end", value: "totalVentaStr", width: 120 },
        { text: "Descuentos", align: "end", value: "totalDescuentosStr", width: 130 },
        { text: "Total IVA", align: "end", value: "totalImpuestoStr", width: 120 },
        { text: "Total Comprobante", align: "end", value: "totalComprobanteStr", width: 160 },
        { text: "PDF", value: "pdf", sortable: false, align: 'center', width: 60 },
        { text: "XML Env.", value: "enviado", sortable: false, align: 'center', width: 70 },
        { text: "XML Resp.", value: "recibido", sortable: false, align: 'center', width: 80 }
      ],
      headers2: [
        { text: "Clave Hacienda", align: "start", value: "clavehacienda", width: 450 },
        { text: "Emisor", align: "start", value: "clienteNombre", width: 300 },
        { text: "Cédula Emisor", align: "start", value: "cedulaReceptor", width: 140 },
        { text: "Fecha", align: "start", value: "fechaString", width: 110 },
        { text: "Moneda", align: "start", value: "moneda", width: 90 },
        { text: "Consecutivo Emisor", align: "start", value: "consecutivoEmisor", width: 200 },
        { text: "Estado Doc Hacienda", align: "start", value: "estadoHaciendaEmisor", width: 170 },
        { text: "Estado Aceptación", align: "start", value: "estadoAceptacion", width: 160 },
        { text: "Cons. Aceptación", align: "start", value: "consHacReal", width: 200 },
        { text: "Documento", align: "start", value: "tipoDocumento", width: 220 },
        { text: "Clasificación", align: "center", value: "clasificacion", width: 140 },
        { text: "Sub Total", align: "end", value: "totalVentaStr", width: 120 },
        { text: "Descuentos", align: "end", value: "totalDescuentosStr", width: 130 },
        { text: "Total IVA", align: "end", value: "totalImpuestoStr", width: 120 },
        { text: "Total Comprobante", align: "end", value: "totalComprobanteStr", width: 160 },
        { text: "XML Recibido", value: "xmlRecibido", sortable: false, align: 'center', width: 100 },
        { text: "Msg. Receptor", value: "xmlFirmado", sortable: false, align: 'center', width: 100 },
        { text: "XML Respuesta", value: "xmlRespuesta", sortable: false, align: 'center', width: 100 }
      ]
    };
  },
  computed: {
    ...mapGetters(["user", "strCia"]),
    isReportGenerated() { return this.items.length > 0 || this.items2.length > 0; },
    emitidosTotals() {
      const sum = (arr, field) => arr.reduce((a, i) => a + (parseFloat(i[field] || i[field.toLowerCase()]) || 0), 0);
      return {
        subTotal: this.formatPrice(sum(this.items, 'totalVenta')),
        descuentos: this.formatPrice(sum(this.items, 'totalDescuentos')),
        totalIVA: this.formatPrice(sum(this.items, 'totalImpuesto')),
        totalComprobante: this.formatPrice(sum(this.items, 'totalComprobante'))
      };
    },
    recibidosTotals() {
      const sum = (arr, field) => arr.reduce((a, i) => a + (parseFloat(i[field] || i[field.toLowerCase()]) || 0), 0);
      const merc = this.items2.filter(i => i.clasificacion === 'M');
      const gasto = this.items2.filter(i => i.clasificacion !== 'M');
      return {
        totalMerc: this.formatPrice(sum(merc, 'totalComprobante')),
        totalGasto: this.formatPrice(sum(gasto, 'totalComprobante')),
        subTotal: this.formatPrice(sum(this.items2, 'totalVenta')),
        descuentos: this.formatPrice(sum(this.items2, 'totalDescuentos')),
        totalIVA: this.formatPrice(sum(this.items2, 'totalImpuesto')),
        totalComprobante: this.formatPrice(sum(this.items2, 'totalComprobante'))
      };
    },
    chartOptions() {
      const isDark = this.$vuetify.theme.dark;
      return {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom', labels: { color: isDark ? '#fff' : '#333', font: { weight: 'bold', size: 10 } } },
          tooltip: { backgroundColor: '#121212', titleColor: '#4db6ac', bodyColor: '#fff', cornerRadius: 8, padding: 12 }
        }
      };
    }
  },
  created() { this.getOpcionesSistemaCia(); },
  methods: {
    getReport() { if(this.dates.length === 2) { this.getEmitidos(); this.getRecibidos(); } },
    processSalesChart() {
      const getVal = (i, prop) => i[prop] || i[prop.toLowerCase()] || 0;
      const total = this.items.reduce((a, b) => a + (parseFloat(getVal(b, 'totalComprobante')) || 0), 0);
      const contado = this.items.filter(i => String(getVal(i, 'condicionVenta')).trim() === '01').reduce((a, b) => a + (parseFloat(getVal(b, 'totalComprobante')) || 0), 0);
      const credito = this.items.filter(i => String(getVal(i, 'condicionVenta')).trim() === '02').reduce((a, b) => a + (parseFloat(getVal(b, 'totalComprobante')) || 0), 0);
      this.totalsVentas = total;
      if (total > 0) {
        const isDark = this.$vuetify.theme.dark;
        const salesColors = isDark ? ['#90caf9', '#ffb74d'] : ['#00897b', '#ffb74d'];
        this.chartDataSales = {
          labels: ['Fact. Contado', 'Fact. Crédito'],
          datasets: [{ backgroundColor: salesColors, data: [contado.toFixed(2), credito.toFixed(2)], borderWidth: 2, borderColor: '#1e1e1e' }]
        };
      }
    },
    processPurchasesChart() {
      let tManualMerc = 0, tManualGasto = 0, tAutoMerc = 0, tAutoGasto = 0;
      
      this.items2.forEach(item => {
        const total = parseFloat(item.totalComprobante || item.totalcomprobante) || 0;
        const status = item.clasificacion;
        
        if (status === 'M') { tManualMerc += total; }
        else { tManualGasto += total; } // Se asume 'S' o cualquier otro como Gasto/Servicio
      });

      this.totalManualMerc = tManualMerc;
      this.totalManualGasto = tManualGasto;
      this.totalAutoMerc = tAutoMerc;
      this.totalAutoGasto = tAutoGasto;
      
      const totalMerc = tManualMerc + tAutoMerc;
      const totalGasto = tManualGasto + tAutoGasto;
      this.totalsInventarioGastos = totalMerc + totalGasto;

      if (this.totalsInventarioGastos > 0) {
        this.chartDataPurchases = {
          labels: ['Inventario/Mercadería', 'Gastos/Servicios'],
          datasets: [{ backgroundColor: ['#3f51b5', '#ff7043'], data: [totalMerc.toFixed(2), totalGasto.toFixed(2)], borderWidth: 2, borderColor: '#1e1e1e' }]
        };
      }
    },
    getEmitidos() {
      axios.get(`api/Reportes/getFacturasEmitidas?cia=${this.strCia}&fechaInicio=${this.dates[0]}&fechaFin=${this.dates[1]}`)
        .then(response => {
          this.items = response.data;
          this.processSalesChart();
          this.checkEmailOption();
        });
    },
    getRecibidos() {
      axios.get(`api/Reportes/getFacturasRecibidas?cia=${this.strCia}&fechaInicio=${this.dates[0]}&fechaFin=${this.dates[1]}`)
        .then(response => {
          this.items2 = response.data.filter(i => (i.clavehacienda || i.claveHacienda));
          this.processPurchasesChart();
        });
    },
    checkEmailOption() {
      if (this.opcionesSistemaCia) {
        const opc = this.opcionesSistemaCia.find(o => o.opcionId === "correo_contador");
        if (opc && opc.valor === "true") this.validEnviarCorreo = true;
      }
    },
    getOpcionesSistemaCia() { axios.get("api/General/getOpcionesSistemaCia?cia=" + this.strCia).then(r => this.opcionesSistemaCia = r.data); },
    sendEmailReport() {
      axios.get(`api/Reportes/enviarCorreoContable?cia=${this.strCia}&fechaInicio=${this.dates[0]}&fechaFin=${this.dates[1]}`)
        .then(r => { this.snackText = r.data; this.snackValue = true; });
    },
    downloadReport() {
      axios.get(`api/Reportes/getReporteContable?cia=${this.strCia}&fechaInicio=${this.dates[0]}&fechaFin=${this.dates[1]}`, { responseType: "blob" })
        .then(r => {
          const url = window.URL.createObjectURL(new Blob([r.data]));
          const link = document.createElement("a"); link.href = url;
          link.setAttribute("download", `Auditoria_${this.dates[0]}.xlsx`); link.click();
        });
    },
    formatPrice(v) { return (v/1).toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, "."); },
    openDocViewer(url, title) { this.docViewerUrl = url; this.docViewerTitle = title; this.docViewerDialog = true; },
    abrirEnNuevaPestana() { window.open(this.docViewerUrl, '_blank'); },
    viewPDF(item) {
      const base = axios.defaults.baseURL || "";
      this.openDocViewer(`${base}/api/Facturacion/getFileContent?cia=${item.cia}&clave=${item.clavehacienda}&type=pdf`, "PDF — Comprobante");
    },
    viewEnviado(item) {
      const base = axios.defaults.baseURL || "";
      this.openDocViewer(`${base}/api/Facturacion/getFileContent?cia=${item.cia}&clave=${item.clavehacienda}&type=signed`, "XML Firmado");
    },
    viewRecibido(item) {
      const base = axios.defaults.baseURL || "";
      this.openDocViewer(`${base}/api/Facturacion/getFileContent?cia=${item.cia}&clave=${item.clavehacienda}&type=response`, "Respuesta Hacienda");
    },
    viewXMLRecibido(item) {
      const base = axios.defaults.baseURL || "";
      this.openDocViewer(`${base}/api/Facturacion/getFileContent?cia=${item.cia}&clave=${item.clavehacienda}&type=recibido`, "XML Recibido del Proveedor");
    },
    viewXMLFirmado(item) {
      const base = axios.defaults.baseURL || "";
      this.openDocViewer(`${base}/api/Facturacion/getFileContent?cia=${item.cia}&clave=${item.clavehacienda}&type=mensaje&consecutivo=${item.consHacReal}`, "Mensaje de Aceptación/Rechazo");
    },
    viewXMLRespuesta(item) {
      const base = axios.defaults.baseURL || "";
      this.openDocViewer(`${base}/api/Facturacion/getFileContent?cia=${item.cia}&clave=${item.clavehacienda}&type=response&consecutivo=${item.consHacReal}`, "Respuesta de Hacienda");
    }
  }
};
</script>

<style scoped>
.audit-main-container { min-height: 100vh; padding-bottom: 80px; font-family: 'Inter', sans-serif !important; }
.theme--dark-bg { background-color: #121212 !important; }
.shadow-premium { box-shadow: 0 10px 30px rgba(0,0,0,0.3) !important; }
.shadow-inner-premium { box-shadow: inset 0 2px 10px rgba(0,0,0,0.2); }

/* SCORECARDS */
.score-card-dark { background: #1e1e1e !important; border: 1px solid #333 !important; }
.score-card-light { background: white !important; border: 1px solid #ebebeb !important; }
.score-icon-wrap { width: 56px; height: 56px; border-radius: 16px; display: flex; align-items: center; justify-content: center; }
.bg-success-soft { background: rgba(76, 175, 80, 0.12) !important; }
.bg-warning-soft { background: rgba(255, 152, 0, 0.12) !important; }
.bg-info-soft { background: rgba(0, 150, 136, 0.12) !important; }
.bg-error-soft { background: rgba(244, 67, 54, 0.12) !important; }
.score-label { font-size: 0.65rem; font-weight: 800; color: #78909c; text-transform: uppercase; letter-spacing: 0.5px; }
.score-value { font-size: 1.6rem; font-weight: 900; letter-spacing: -0.5px; }

/* HEADER & FILTERS */
.audit-header { border-bottom: 1px solid #e0e0e0 !important; }
.theme--dark .audit-header { background: #121212 !important; border-color: #333 !important; }
.audit-title { font-size: 1.4rem !important; font-weight: 900 !important; letter-spacing: -0.5px; }
.filter-dense-panel { border: 1px solid #cfd8dc !important; }
.theme--dark .filter-dense-panel { background: #1e1e1e !important; border-color: #333 !important; }
.theme--dark .audit-input >>> .v-input__control >>> .v-input__slot { background: #2a2a2a !important; }

/* SUMMARY BAR */
.summary-bar-container { display: flex; background: white; border: 1px solid #cfd8dc; }
.theme--dark .summary-bar-container { background: #1a1a1a; border-color: #333; }
.summary-label-mini { font-size: 0.6rem; color: #90a4ae; text-transform: uppercase; font-weight: 800; }
.summary-value-mini { font-size: 1.1rem; }
.border-right-premium { border-right: 1px solid #cfd8dc !important; }
.theme--dark .border-right-premium { border-right: 1px solid #333 !important; }
.bg-summary-total-sales { background: linear-gradient(135deg, #00796b 0%, #004d40 100%) !important; }

/* CHARTS */
.audit-chart-card-dark { background: #1e1e1e !important; border: 1px solid #333 !important; }
.audit-chart-card-light { background: white !important; border: 1px solid #cfd8dc !important; }

/* TABLES */
.audit-table { border-radius: 0 !important; }
.theme--dark .audit-table { background: #121212 !important; }
.audit-table >>> th { background: #263238 !important; color: white !important; font-size: 0.75rem !important; font-weight: 800 !important; text-transform: uppercase; }
.theme--dark .audit-table >>> th { background: #1e1e1e !important; border-bottom: 1px solid #333 !important; }
.theme--dark .audit-table >>> td { border-bottom: 1px solid #222 !important; font-size: 0.85rem !important; }
.audit-zebra >>> tbody tr:nth-of-type(odd) { background: #fcfcfc; }
.theme--dark .audit-zebra >>> tbody tr:nth-of-type(odd) { background: #1a1a1a !important; }

.opacity-70 { opacity: 0.75; }
.doc-viewer-toolbar { background: linear-gradient(135deg, #37474f 0%, #455a64 100%); min-height: 44px; }
.fade-enter-active { transition: opacity .6s cubic-bezier(0.4, 0, 0.2, 1); }
.fade-enter { opacity: 0; }

/* FOOTER TOTALES */
.rc-footer { border-top: 3px solid #37474f; background: #fff; }
.rc-footer--dark { background: #1a1a1a !important; border-top-color: #444 !important; }
.rc-footer__row { display: flex; align-items: center; flex-wrap: wrap; padding: 8px 16px; gap: 8px; }
.rc-footer__row--technical { background: rgba(55, 71, 79, 0.06); border-bottom: 1px solid #e0e0e0; padding: 6px 16px; }
.rc-footer--dark .rc-footer__row--technical { background: rgba(255,255,255,0.04); border-bottom-color: #333; }
.rc-footer__row--summary { padding: 10px 16px; }
.rc-footer__item { display: flex; align-items: center; margin-right: 16px; }
.rc-footer__label { font-size: 0.65rem; font-weight: 800; color: #78909c; text-transform: uppercase; letter-spacing: 0.5px; }
.rc-footer__value { font-size: 0.95rem; margin-left: 6px; }
.rc-footer__count { font-size: 0.65rem; font-weight: 800; color: #90a4ae; white-space: nowrap; }
</style>